import React, {
  Children,
  createContext,
  forwardRef,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { faXmark, IconDefinition } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { Button, Flex } from 'antd'
import styled from 'styled-components'

import useDisclosure from '../../../hooks/use-disclosure'
import { getModalRoot } from '../Portal/Portal'
import { T } from '../Text/Text'
import Tooltip from '../Tooltip/Tooltip'

const Overlay = styled.div`
  width: 100vw;
  height: 100vh;
  background-color: ${({ theme }) => theme.colorBgMask};
  position: fixed;
  inset: 0;
  overflow-y: auto;
  animation: overlayShow 150ms cubic-bezier(0.16, 1, 0.3, 1);
  @keyframes overlayShow {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

const OverlayLayout = styled.div<{ $centered?: boolean }>`
  display: flex;
  justify-content: center;
  width: 100%;
  min-height: 100%;
  ${({ $centered }) => ($centered ? 'align-items: center;' : 'align-items: flex-start;')}
  padding: ${({ $centered }) => ($centered ? '0' : '80px 0')};
  @media (max-width: 768px) {
    padding: ${({ $centered }) => ($centered ? '0' : '20px 0')};
  }
`

const Panel = styled(DialogPrimitive.Content)<{
  $height?: React.CSSProperties['height']
  $width?: React.CSSProperties['width']
}>`
  position: relative;
  background-color: ${({ theme }) => theme.colorWhite};
  border-radius: 6px;
  box-shadow: ${({ theme }) => theme.boxShadowSecondary};
  padding: 0;
  width: 100%;
  max-width: ${(props) => props.$width || '500px'};
  height: ${(props) => props.$height || 'fit-content'};
  display: flex;
  flex-direction: column;
  align-items: stretch;

  max-height: calc(100vh - 160px);
  @supports (height: 100dvh) {
    max-height: calc(100dvh - 160px);
  }
  @media (max-width: 768px) {
    max-height: calc(100vh - 40px);
    @supports (height: 100dvh) {
      max-height: calc(100dvh - 40px);
    }
  }

  &:focus {
    outline: none;
  }

  animation: contentShow 150ms cubic-bezier(0.16, 1, 0.3, 1);
  @keyframes contentShow {
    from {
      opacity: 0;
      transform: scale(0.96);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`

const Header = styled.header`
  padding: 12px 16px;
  margin: 0;
  border-bottom: solid 1px ${({ theme }) => theme.colorSplit};
`

const Title = styled(DialogPrimitive.Title)`
  margin: 0;
  width: 100%;
`

const CloseButton = styled(DialogPrimitive.Close)`
  height: 24px;
  width: 24px;
  padding: 0;
  margin: 0;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colorWhite};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.1s;

  &:focus-visible {
    outline: solid 2px ${({ theme }) => theme.colorPrimaryBorder};
  }

  &:hover {
    background-color: ${({ theme }) => theme.colorBgTextHover};
  }
`

const Main = styled.div`
  padding: 20px;
  overflow-y: auto;
  flex: 1;
  height: 100%;
`

const Footer = styled.footer`
  padding: 8px 12px;
  border-top: solid 1px ${({ theme }) => theme.colorSplit};
  height: fit-content;
  min-height: fit-content;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;

  &:empty {
    padding: 0px;
  }
`

const stopPropagation = (e: React.MouseEvent<HTMLElement>) => {
  e.stopPropagation()
}

/**
 * Radix `modal` dialogs use RemoveScroll / aria-hidden on the document. If the tree
 * unmounts while `open` is still true (parent `onClose` without `open={false}`), that
 * lock can persist and block interaction with the page behind the dialog.
 */
export const releaseModalBodyLock = () => {
  document.body.style.pointerEvents = ''
  document.body.removeAttribute('data-scroll-locked')
  document.body.style.overflow = ''
  document.body.removeAttribute('aria-hidden')
  document.body.removeAttribute('inert')
  document.documentElement.removeAttribute('data-scroll-locked')
  document.documentElement.style.overflow = ''
  document.querySelectorAll('[data-radix-focus-guard]').forEach((node) => node.remove())
  // Radix modal mode marks app roots inert/aria-hidden; stale attrs block nav clicks after unmount.
  document.querySelectorAll('[data-aria-hidden="true"], [aria-hidden="true"]').forEach((node) => {
    if (node === document.body) {
      return
    }
    node.removeAttribute('aria-hidden')
    node.removeAttribute('data-aria-hidden')
    node.removeAttribute('inert')
  })
}

/** Title-case visible modal header text; leaves acronyms like GL unchanged. */
export const toModalHeaderTitle = (title: string) =>
  title.replace(/\b[A-Za-z][A-Za-z']*\b/g, (word) => {
    if (/^[A-Z]{2,}$/.test(word)) {
      return word
    }
    return word.charAt(0).toUpperCase() + word.slice(1)
  })

type ModalContextType = {
  onClose: () => void
  /** Dialog panel element — portaled popovers should mount here to stay inside Radix dismissable layer. */
  portalContainer: HTMLElement | null
}
const ModalContext = createContext({} as ModalContextType)
const useModalContext = () => useContext(ModalContext)

/** When rendered inside `Modal`, returns the panel element for popover/select portals. */
export const useModalPortalContainer = () => useContext(ModalContext).portalContainer

type ModalHeaderProps = {
  children: React.ReactNode
  /** When true, the header close (X) control is omitted (e.g. acknowledgment-only modals). */
  hideCloseButton?: boolean
  icon?: IconDefinition
}
const ModalHeader = ({ children, hideCloseButton, icon }: ModalHeaderProps) => {
  const { onClose } = useModalContext()

  return (
    <Header onClick={stopPropagation}>
      <Flex
        justify={hideCloseButton ? 'flex-start' : 'space-between'}
        align="baseline"
        className={
          hideCloseButton
            ? 'flex flex-row items-baseline justify-start'
            : 'flex flex-row-reverse items-baseline justify-between'
        }
      >
        {!hideCloseButton && (
          <CloseButton
            data-testid="modal-close-btn"
            onClick={(e) => {
              e.preventDefault()
              onClose()
            }}
          >
            <FontAwesomeIcon icon={faXmark} />
          </CloseButton>
        )}
        <Title>
          <Flex align="baseline" gap="8px">
            {icon && (
              <T.BodyLarge className="text-orange-700">
                <FontAwesomeIcon icon={icon} />
              </T.BodyLarge>
            )}
            {typeof children === 'string' ? (
              <T.BodyLarge className="font-semibold">{toModalHeaderTitle(children)}</T.BodyLarge>
            ) : (
              children
            )}
          </Flex>
        </Title>
      </Flex>
    </Header>
  )
}

const childrenIncludeElementType = (
  children: React.ReactNode,
  elementType: React.ElementType
): boolean =>
  Children.toArray(children).some((child) => {
    if (!isValidElement(child)) {
      return false
    }
    if (child.type === elementType) {
      return true
    }
    const nested = (child.props as { children?: React.ReactNode }).children
    return nested ? childrenIncludeElementType(nested, elementType) : false
  })

/** Walks modal children to detect a visible `Modal.Header` (Radix `DialogTitle`). */
const modalChildrenIncludeHeader = (children: React.ReactNode): boolean =>
  childrenIncludeElementType(children, ModalHeader)

type Props = {
  /**
   * Accessible name when no visible `Modal.Header` is rendered.
   * Every modal must pass `aria-label` or include `Modal.Header`.
   */
  'aria-label'?: string
  centered?: boolean
  children: React.ReactNode
  className?: string
  height?: React.CSSProperties['height']
  /**
   * Invoked after Radix closes the dialog (`open` → false). Use for parent unmount /
   * disclosure cleanup — not for intercepting close (use `onCloseIntent`).
   */
  onClose: () => void
  /**
   * Optional hook for header/footer close controls (unsaved-changes prompts, etc.).
   * When omitted, those controls dismiss the dialog directly.
   */
  onCloseIntent?: () => void
  /**
   * Registers `dismissDialog` so callers outside `Modal` children (e.g. after async
   * submit) can close through Radix before the parent unmounts.
   */
  onBindDismiss?: (dismiss: () => void) => void
  /**
   * Customize which element receives focus when the modal closes; by default it is the element that triggered the modal, but you will need to set this if the trigger no longer exists (e.g. a menu item)
   */
  onCloseFocusRef?: React.RefObject<HTMLElement>
  /**
   * When true, clicking the overlay backdrop will not close the modal.
   * The user must explicitly use the close button, cancel button, or Escape key.
   */
  preventOverlayClose?: boolean
  width?: React.CSSProperties['width']
}

const Modal = ({
  'aria-label': ariaLabel,
  centered,
  children,
  className,
  height,
  onClose,
  onCloseIntent,
  onBindDismiss,
  onCloseFocusRef,
  preventOverlayClose,
  width,
}: Props) => {
  const {
    isOpen: isDialogOpen,
    open: openDialog,
    close: closeDialog,
  } = useDisclosure({ initialOpen: true })
  const hasNotifiedCloseRef = useRef(false)
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null)
  const [lastFocusedElement, setLastFocusedElement] = useState<HTMLElement | null>(null)

  const handlePanelRef = useCallback((node: HTMLDivElement | null) => {
    setPortalContainer(node)
  }, [])

  useEffect(() => {
    setLastFocusedElement(document.activeElement as HTMLElement)
  }, [])

  const notifyClosed = useCallback(() => {
    if (hasNotifiedCloseRef.current) {
      return
    }
    hasNotifiedCloseRef.current = true
    releaseModalBodyLock()
    requestAnimationFrame(() => releaseModalBodyLock())
    onClose()
  }, [onClose])

  // Close through Radix (`open` → false) so RemoveScroll/aria-hidden cleanup runs before parent unmount.
  const dismissDialog = useCallback(() => {
    closeDialog()
  }, [closeDialog])

  const contextOnClose = onCloseIntent ?? dismissDialog

  useEffect(() => {
    if (isDialogOpen) {
      hasNotifiedCloseRef.current = false
      return
    }
    releaseModalBodyLock()
    const frameId = requestAnimationFrame(() => releaseModalBodyLock())
    notifyClosed()
    return () => cancelAnimationFrame(frameId)
  }, [isDialogOpen, notifyClosed])

  useEffect(() => {
    onBindDismiss?.(dismissDialog)
    return () => onBindDismiss?.(() => {})
  }, [dismissDialog, onBindDismiss])

  // Parent may unmount this tree while Radix still considers the dialog open (e.g. refetch
  // removes the trigger row). Schedule extra lock releases so the page stays clickable.
  useEffect(() => {
    return () => {
      releaseModalBodyLock()
      requestAnimationFrame(() => releaseModalBodyLock())
      window.setTimeout(() => releaseModalBodyLock(), 0)
    }
  }, [])

  const hasVisibleHeader = modalChildrenIncludeHeader(children)

  useEffect(() => {
    if (ariaLabel || hasVisibleHeader) {
      return
    }
    console.error(
      '[Modal] Provide aria-label or Modal.Header so the dialog has an accessible name.'
    )
  }, [ariaLabel, children, hasVisibleHeader])

  const handleCloseAutoFocus = useCallback(
    (e: Event) => {
      e.preventDefault()
      setTimeout(() => {
        const restoreFocusElement = onCloseFocusRef?.current || lastFocusedElement
        if (restoreFocusElement) {
          restoreFocusElement.focus()
        }
      })
    },
    [lastFocusedElement, onCloseFocusRef]
  )

  const handleEscapeKeyDown = useCallback(
    (e: KeyboardEvent) => {
      e.stopPropagation()
      e.preventDefault()
      contextOnClose()
    },
    [contextOnClose]
  )

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation()
      if (!preventOverlayClose) {
        contextOnClose()
      }
    },
    [contextOnClose, preventOverlayClose]
  )

  const handleInteractOutside = useCallback((e: Event) => e.preventDefault(), [])

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        openDialog()
      } else {
        closeDialog()
      }
    },
    [closeDialog, openDialog]
  )

  const modalContextValue = useMemo(
    () => ({ onClose: contextOnClose, portalContainer }),
    [contextOnClose, portalContainer]
  )

  return (
    <DialogPrimitive.Root open={isDialogOpen} onOpenChange={handleOpenChange} modal>
      <DialogPrimitive.Portal container={getModalRoot()}>
        <Overlay onClick={handleOverlayClick}>
          <OverlayLayout $centered={centered}>
            <Panel
              $height={height}
              $width={width}
              aria-describedby={undefined}
              onClick={stopPropagation}
              onCloseAutoFocus={handleCloseAutoFocus}
              onEscapeKeyDown={handleEscapeKeyDown}
              onInteractOutside={handleInteractOutside}
              className={className}
            >
              <ModalContext.Provider value={modalContextValue}>
                <div
                  ref={handlePanelRef}
                  className="flex min-h-0 w-full flex-1 flex-col items-stretch"
                >
                  {ariaLabel && !hasVisibleHeader ? (
                    <VisuallyHidden>
                      <DialogPrimitive.Title>{ariaLabel}</DialogPrimitive.Title>
                    </VisuallyHidden>
                  ) : null}
                  {children}
                </div>
              </ModalContext.Provider>
            </Panel>
          </OverlayLayout>
        </Overlay>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

type ModalContentProps = {
  className?: string
  children: React.ReactNode
}
const ModalContent = ({ children, className }: ModalContentProps) => {
  return (
    <Main className={className} onClick={stopPropagation}>
      {typeof children === 'string' ? <T.Body>{children}</T.Body> : children}
    </Main>
  )
}

type ModalFooterProps =
  | {
      cancelText?: never
      children: React.ReactNode
      confirmText?: never
      confirmTestId?: never
      isDestructive?: never
      isEmptyFooterShell?: never
      isLoading?: never
      disabledReason?: never
      onCancel?: never
      onConfirm?: never
    }
  | {
      cancelText?: string
      children?: never
      confirmText?: string
      confirmTestId?: string
      isDestructive?: boolean
      isEmptyFooterShell?: never
      isLoading?: boolean
      disabledReason?: string
      onConfirm?: (() => void) | (() => Promise<unknown>)
    }
  // Renders an empty footer shell (e.g. for portaled content).
  | {
      isEmptyFooterShell: true
      children?: never
      cancelText?: never
      confirmText?: never
      confirmTestId?: never
      isDestructive?: never
      isLoading?: never
      disabledReason?: never
      onCancel?: never
      onConfirm?: never
    }
const ModalFooter = forwardRef<HTMLElement, ModalFooterProps>(
  (
    {
      children,
      cancelText,
      confirmText,
      confirmTestId,
      isDestructive,
      isEmptyFooterShell,
      isLoading,
      disabledReason,
      onConfirm,
    }: ModalFooterProps,
    ref
  ) => {
    const { onClose } = useModalContext()
    const { isOpen: isConfirming, open: startConfirming, close: stopConfirming } = useDisclosure()
    const handleConfirm = useCallback(async () => {
      if (isConfirming) {
        return
      }

      try {
        startConfirming()
        await onConfirm?.()
      } catch {
        // onConfirm rejected (validation, mutation failure, etc.) — keep modal open
        return
      } finally {
        stopConfirming()
      }
      releaseModalBodyLock()
      onClose()
    }, [isConfirming, onClose, onConfirm, startConfirming, stopConfirming])

    return (
      <Footer ref={ref} onClick={stopPropagation}>
        {children !== undefined ? (
          children
        ) : isEmptyFooterShell ? null : (
          <>
            <Button data-testid="modal-cancel-btn" onClick={onClose}>
              {cancelText ?? 'Cancel'}
            </Button>
            <Tooltip content={disabledReason}>
              <Button
                type="primary"
                onClick={handleConfirm}
                danger={isDestructive}
                loading={isLoading || isConfirming}
                disabled={!!disabledReason}
                data-testid={confirmTestId}
              >
                {confirmText ?? 'Confirm'}
              </Button>
            </Tooltip>
          </>
        )}
      </Footer>
    )
  }
)

export default Object.assign(Modal, {
  Content: ModalContent,
  Footer: ModalFooter,
  Header: ModalHeader,
})
